import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Tenir bon quand l'application passe derriere.
 *
 * iOS suspend une vue web des qu'on la quitte : la requete en cours est
 * coupee net. Envoyer une video de quatre mega-octets puis changer
 * d'application, c'est perdre l'envoi — et le message ne part jamais,
 * sans que rien ne le dise. Le meme sort attend une photo sur un reseau
 * lent, ou un vocal.
 *
 * Le systeme accorde un sursis d'une trentaine de secondes a qui le
 * demande. Ce n'est pas un envoi en arriere-plan au sens strict — il
 * faudrait une `URLSession` native, donc reecrire le televersement
 * Firebase — mais cela couvre le cas courant : on quitte une seconde, on
 * revient, et le message est parti.
 *
 * Sans effet hors de l'application native : sur le site deploye, un
 * onglet quitte continue de tourner de lui-meme.
 */

interface ArrierePlan {
  retenir(): Promise<{ id: string }>;
  relacher(options: { id: string }): Promise<void>;
}

const greffon = registerPlugin<ArrierePlan>('ArrierePlan');

function disponible(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('ArrierePlan');
}

/**
 * Execute un travail sous la protection du sursis.
 *
 * Le sursis est rendu dans tous les cas, succes comme echec : une tache
 * jamais close fait tuer l'application par le systeme. C'est la raison
 * du `finally`, et la raison pour laquelle ce module n'expose que cette
 * forme plutot que deux appels a apparier soi-meme.
 */
export async function pendant<T>(travail: () => Promise<T>): Promise<T> {
  if (!disponible()) return travail();

  const jeton = await greffon
    .retenir()
    .then((r) => r.id)
    .catch(() => null);

  try {
    return await travail();
  } finally {
    if (jeton) await greffon.relacher({ id: jeton }).catch(() => undefined);
  }
}
